import {
	styled,
	Stepper,
	Step,
	StepLabel,
	StepConnector,
	StepperProps,
	StepIconProps,
	Tooltip,
	StepLabelProps,
	StepConnectorProps,
	StepButton,
	StepButtonProps,
	createTheme,
	ThemeProvider,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";

import CheckIcon from "../images/check-icon.svg?react";
import VerticalCheckIcon from "../images/vertical-stepper-checkmark.svg?react";
import i18nPackage from "../../i18n/i18n";
import { FONT_FAMILY } from "../../theme";
import styles from "./Stepper.module.css";

const coreStepperTheme = (vertical: boolean) =>
	createTheme({
		components: {
			MuiStepper: {
				styleOverrides: {
					root: {
						columnGap: "0px",
						display: "flex",
						justifyContent: vertical ? "flex-start" : "center",
						width: "100%",
						alignItems: "stretch",
						padding: vertical ? "20px 4px 30px 4px" : "initial",
						backgroundColor: vertical ? "#FBFBFB" : "#fff",
						height: vertical ? "100%" : "unset",
						gap: vertical ? "10px" : "initial",
						borderRadius: vertical ? "20px" : "initial",
						border: vertical ? "1px solid #e2e2e2" : "initial",
					},
				},
			},
			MuiStep: {
				styleOverrides: {
					root: {
						display: "flex",
						justifyContent: "center",
						alignItems: "flex-start",
						padding: 0,

						...(vertical && {
							'& > .MuiStepButton-root[aria-current="step"]': {
								"&, & .MuiStepLabel-root": {
									backgroundColor: "#F4F5FC",
									borderRadius: 8,
								},
							},
						}),
					},
				},
			},
			MuiStepButton: {
				styleOverrides: {
					root: {
						borderRadius: "4px",
						padding: vertical ? 0 : "6px",
						margin: 0,
						"&:hover": {
							backgroundColor: "#f5f5f5",
						},
						"&:focus-visible": {
							outline: "2px solid var(--focus-outline-color-dark)",
							outlineOffset: "2px",
						},
						"&:focus-visible:hover": {
							backgroundColor: "inherit",
						},
					},
				},
			},
			MuiStepLabel: {
				styleOverrides: {
					root: {
						display: "flex",
						justifyContent: vertical ? "flex-start" : "center",
						alignItems: "center",
						textAlign: vertical ? "left" : "center",
						flexDirection: vertical ? "row" : "column",
						padding: vertical ? "10px" : 0,
						width: "100%",
						gap: vertical ? "10px !important" : "16px",
					},
					label: {
						fontSize: "15px",
						transition: "color 100ms",
						textAlign: vertical ? "left" : "center",
						color: vertical ? "#727272" : "#6F6F6F",
						fontWeight: vertical ? "500" : "400",
						justifyContent: vertical ? "flex-start" : "intiial",
						display: "flex",
						"&.Mui-completed": {
							color: vertical ? "#727272" : "#27772D",
							fontWeight: vertical ? "500" : "600",
						},
						"&.Mui-active": {
							color: "#42296e",
							fontWeight: "600",
							...(vertical && {
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}),
						},
					},
					iconContainer: {
						padding: vertical ? "initial" : "0px",
					},
					alternativeLabel: {
						"& $label": {
							marginTop: "4px",
						},
					},
					labelContainer: {
						flexGrow: 1,
					},
				},
			},
			MuiStepIcon: {
				styleOverrides: {
					text: {
						fontFamily: FONT_FAMILY,
					},
				},
			},
			MuiStepConnector: {
				styleOverrides: {
					root: {
						margin: "8px 6px", // So focus outline of step doesn't overlap connector
						minWidth: "15px",
						alignSelf: "anchor-center",
					},
					line: {
						height: "0.37px",
						border: 0,
						backgroundColor: "#000000",
					},
				},
			},
		},
	});

interface CoreStepLabelProps extends StepLabelProps {
	usesTooltips?: boolean;
	active?: boolean;
	completed?: boolean;
	noWrap?: boolean;
}

interface CoreStepConnectorProps extends StepConnectorProps {
	reducedConnectorWidth?: boolean;
}

const CoreStepLabel = styled(StepLabel, {
	shouldForwardProp: (prop) => prop !== "usesTooltips" && prop !== "noWrap",
})<CoreStepLabelProps>((props) => ({
	gap: props.usesTooltips ? "0px" : "16px",
	marginRight: props.usesTooltips ? "10px" : undefined,
	marginLeft: props.usesTooltips ? "10px" : undefined,
	whiteSpace: props.noWrap ? "nowrap" : undefined,
}));

const CoreStepperConnector = styled(StepConnector)<CoreStepConnectorProps>((props) => ({
	[`&`]: {
		flex: props.reducedConnectorWidth ? "0.3 1 0" : "0.5 1 0",
	},
}));

const CoreStepperStepIconRoot = styled("div")<{
	ownerState: { completed?: boolean; active?: boolean; vertical: boolean };
}>(({ ownerState }) => ({
	zIndex: 1,
	color: ownerState.vertical ? "#727272" : "#fff",
	fontSize: "14px",
	fontWeight: ownerState.vertical ? "600" : "700",
	lineHeight: "0",
	width: 28,
	height: 28,
	display: "flex",
	borderRadius: "50%",
	justifyContent: "center",
	alignItems: "center",
	fontFamily: FONT_FAMILY,
	...(ownerState?.active
		? {
				backgroundColor: "#42296e",
				...(ownerState.vertical && {
					border: "1px solid #9288cb",
					backgroundColor: "#d4cfef",

					// 2px white gap  inwards
					boxShadow: "inset 0 0 0 2px #fff",

					color: "#42296e",
					boxSizing: "border-box",
				}),
			}
		: ownerState?.completed
			? { backgroundColor: "#27772D" }
			: { backgroundColor: ownerState.vertical ? "#ececf0" : "#6f6f6f" }),
	transition: "all 100ms",
}));

interface CoreStepIconProps extends StepIconProps {
	vertical: boolean;
}

const CoreStepIcon = ({ active, completed, icon, className, vertical }: CoreStepIconProps) => {
	const showCompleteIcon = completed && !active;

	return vertical ? (
		showCompleteIcon ? (
			<VerticalCheckIcon>
				<title>{i18nPackage.t("coreComponents.stepper.complete")}</title>
			</VerticalCheckIcon>
		) : (
			<CoreStepperStepIconRoot ownerState={{ completed, active, vertical }} className={className}>
				{icon}
			</CoreStepperStepIconRoot>
		)
	) : (
		<CoreStepperStepIconRoot ownerState={{ completed, active, vertical }} className={className}>
			{showCompleteIcon ? (
				<CheckIcon>
					<title>{i18nPackage.t("coreComponents.stepper.complete")}</title>
				</CheckIcon>
			) : (
				icon
			)}
		</CoreStepperStepIconRoot>
	);
};

interface CoreStepButtonProps extends StepButtonProps {
	isActive?: boolean;
}

const CoreStepButton = ({ children, isActive, ...props }: CoreStepButtonProps) => (
	<StepButton
		disableRipple
		tabIndex={isActive ? -1 : 0}
		sx={{
			// UX team wants no styling for active step
			...(isActive && {
				pointerEvents: "none",
				"&:focus-visible": {
					outline: "none",
				},
				"&:hover": {
					backgroundColor: "inherit",
				},
			}),
		}}
		{...props}
	>
		{children}
	</StepButton>
);

type CoreStepperProps = {
	variant?: "default" | "scrollable";
	steps: string[] | { name: string; label: string }[];
	usesTooltips?: boolean;
	/**If provided, makes stepper non linear */
	onStepChange?: (step: number) => void;
	/**Used only in non linear mode. */
	completedSteps?: number[];
	/** Apply whiteSpace: nowrap to the CoreLabel */
	noWrapLabel?: boolean;
	vertical?: boolean;
} & Omit<StepperProps, "connector" | "children" | "variant">;

export function CoreStepper({
	variant = "default",
	steps,
	usesTooltips,
	onStepChange,
	completedSteps = [],
	noWrapLabel,
	vertical = false,
	...stepperProps
}: CoreStepperProps) {
	const activeStep = stepperProps.activeStep ?? 0;
	const isNonLinear = typeof onStepChange === "function";
	const firstIncompleteIndex = steps.findIndex((_, index) => !completedSteps.includes(index));
	const lastAccessibleStep = firstIncompleteIndex === -1 ? steps.length - 1 : firstIncompleteIndex - 1;
	const stepperAriaLabel = i18nPackage.t("coreComponents.stepper.ariaLabel", { count: steps.length });
	const stepperRef = useRef<HTMLDivElement>(null);
	const [gradientOpacity, setGradientOpacity] = useState(0);

	useEffect(() => {
		if (variant !== "scrollable" || !stepperRef.current) return;
		const stepperCurrent = stepperRef.current;

		const updateGradient = () => {
			const maxScroll = stepperCurrent.scrollWidth - stepperCurrent.clientWidth;
			const scrollRemaining = maxScroll - stepperCurrent.scrollLeft;

			setGradientOpacity(Math.max(0, Math.min(scrollRemaining / 60, 1)));
		};

		stepperCurrent.addEventListener("scroll", updateGradient);
		updateGradient();

		return () => stepperCurrent.removeEventListener("scroll", updateGradient);
	}, [variant]);

	const stepperContent = (
		<ThemeProvider theme={coreStepperTheme(vertical)}>
			<Stepper
				{...stepperProps}
				connector={vertical ? null : <CoreStepperConnector reducedConnectorWidth={usesTooltips} />}
				aria-label={stepperAriaLabel}
				nonLinear={isNonLinear}
				role="list"
				orientation={vertical ? "vertical" : "horizontal"}
			>
				{steps.map((step, index) => {
					const displayedLabel = typeof step === "string" ? step : step.label;
					const isActive = index === activeStep;
					const isCompleted = completedSteps.includes(index); // Only for non linearity
					const isAccessible = isCompleted && index <= lastAccessibleStep;
					const isNextStep = index === lastAccessibleStep + 1;
					const isDisabled = isNonLinear ? !(isActive || isAccessible || isNextStep) : true;

					const stepLabel = (
						<CoreStepLabel
							active={isActive}
							completed={isCompleted}
							StepIconComponent={(props: StepIconProps) => <CoreStepIcon {...props} vertical={vertical} />}
							usesTooltips={usesTooltips}
							noWrap={noWrapLabel}
						>
							{!usesTooltips && displayedLabel}
						</CoreStepLabel>
					);

					const stepLabelWithButton = isNonLinear ? (
						<CoreStepButton
							onClick={() => onStepChange?.(index)}
							disabled={isDisabled}
							aria-current={isActive ? "step" : undefined}
							aria-label={i18nPackage.t("coreComponents.stepper.stepAriaLabel", {
								index: index + 1,
								count: steps.length,
								status: isActive
									? i18nPackage.t("coreComponents.stepper.current")
									: isCompleted
										? i18nPackage.t("coreComponents.stepper.completed")
										: i18nPackage.t("coreComponents.stepper.incomplete"),
								label: displayedLabel,
							})}
							isActive={isActive}
						>
							{stepLabel}
						</CoreStepButton>
					) : (
						stepLabel
					);

					const labelContent = usesTooltips ? (
						<Tooltip title={displayedLabel} placement="top">
							{stepLabelWithButton}
						</Tooltip>
					) : (
						stepLabelWithButton
					);

					return (
						<Step
							key={displayedLabel}
							active={isActive}
							role="listitem"
							{...(isNonLinear && { completed: isCompleted })} // Add completed prop if in nonLinear. linear stepper tracks complete status automatically
						>
							{labelContent}
						</Step>
					);
				})}
			</Stepper>
		</ThemeProvider>
	);

	if (variant === "scrollable")
		return (
			<div className={styles.stepperWrapper}>
				<div className={styles.stepper} ref={stepperRef}>
					{stepperContent}
				</div>
				<div className={styles.scrollGradient} style={{ opacity: gradientOpacity }} />
			</div>
		);

	return stepperContent;
}
